/**
 * Groq LLM Provider - v13 Section 6.10-6.11
 *
 * Provider for Groq's ultra-fast LLM inference.
 *
 * Key features:
 * - Fastest inference in market (~10x faster)
 * - Zero Data Retention option in Console
 * - OpenAI-compatible API format
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import { BaseLLMProvider, LLMProviderType } from './base';
import { calculateCost } from './registry';
/**
 * Groq LLM Provider - Uses OpenAI-compatible API
 */
export class GroqProvider extends BaseLLMProvider {
    constructor(config) {
        super(config);
        if (!config.apiKey) {
            throw new Error('Groq API key is required');
        }
        this.apiKey = config.apiKey;
        this.baseUrl = config.baseUrl ?? 'https://api.groq.com/openai/v1';
        // Model should be explicitly configured via config.model or request.model
        // Empty default will cause error if no model specified - this is intentional
        this.defaultModel = config.model ?? '';
        // Token limit should come from ConfigService.getModelMetadata() at runtime
        this.defaultMaxTokens = config.maxTokens ?? 4096;
        this.defaultTemperature = config.temperature ?? 0.7;
    }
    getProviderType() {
        return LLMProviderType.GROQ;
    }
    /**
     * Get supported models
     * @deprecated Use configService.getModelsByProvider('groq') for dynamic model list
     */
    getSupportedModels() {
        // No hardcoded model list - use ConfigService for actual list
        return [];
    }
    async isAvailable() {
        try {
            const response = await fetch(`${this.baseUrl}/models`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${this.apiKey}`,
                },
            });
            return response.ok;
        }
        catch (error) {
            this.logger.debug(`Groq availability check failed: ${error}`);
            return false;
        }
    }
    async complete(request) {
        const model = request.model ?? this.defaultModel;
        // Trust caller's maxTokens if provided, otherwise use default
        const maxTokens = request.maxTokens ?? this.defaultMaxTokens;
        const temperature = request.temperature ?? this.defaultTemperature;
        // Validate request
        const errors = this.validateRequest(request);
        if (errors.length > 0) {
            return this.createErrorResponse(errors.join('; '), model);
        }
        const messages = request.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
        }));
        this.logger.info(`Using model: ${model} for Groq request`);
        const startTime = Date.now();
        try {
            const response = await fetch(`${this.baseUrl}/chat/completions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.apiKey}`,
                },
                body: JSON.stringify({
                    model,
                    messages,
                    max_tokens: maxTokens,
                    temperature,
                }),
            });
            if (!response.ok) {
                const error = await response.text();
                throw new Error(`Groq API error: ${response.status} ${error}`);
            }
            const data = await response.json();
            const processingTime = (Date.now() - startTime) / 1000;
            const content = data.choices?.[0]?.message?.content ?? '';
            const inputTokens = data.usage?.prompt_tokens ?? 0;
            const outputTokens = data.usage?.completion_tokens ?? 0;
            const totalTokens = data.usage?.total_tokens ?? 0;
            const costUsd = calculateCost(model, inputTokens, outputTokens);
            this.logger.debug('Received response from Groq', {
                model,
                content_length: content.length,
                processing_time: processingTime,
                tokens_used: totalTokens,
                cost: costUsd,
            });
            return {
                content,
                model,
                usage: {
                    inputTokens,
                    outputTokens,
                    totalTokens,
                    costUsd,
                },
                finishReason: data.choices?.[0]?.finish_reason,
            };
        }
        catch (error) {
            const processingTime = (Date.now() - startTime) / 1000;
            const errorMsg = `Groq API error: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error('Groq generation failed', { model, error: errorMsg, processingTime });
            return this.createErrorResponse(errorMsg, model);
        }
    }
}
